import { useMutation, useQueryClient } from '@tanstack/react-query';

import { secretKeys } from '@/features/secrets';
import { updateConnection } from '@/lib/api/generated/api';
import type {
  ConnectionUpdate,
  ConnectionView,
} from '@/lib/api/generated/model';

import { toConnectionError } from '../errors';
import { connectionKeys } from '../queryKeys';

/**
 * Edit a connection's non-secret-shape details (name, description, enabled,
 * config) — never its `type`.
 *
 * Takes the generated `ConnectionUpdate` wire shape directly as the mutation
 * variable, rather than a form-values transformer: `ConnectionUpdate` has no
 * `type` field at all (the backend model), so "update never sends type" is
 * enforced by the type system here, not by a runtime check. Every field
 * defaults to "leave unchanged" server-side — a caller only needs to name
 * what's actually changing.
 *
 * Invalidates both the list (US1's row, e.g. a changed enabled state or
 * name) and the detail read — same reasoning `useSetTriggerProfileEnabled`
 * documents for its own pair of invalidations.
 */
export function useUpdateConnection(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      connectionId,
      data,
    }: {
      connectionId: string;
      data: ConnectionUpdate;
    }): Promise<ConnectionView> => {
      const response = await updateConnection(workspaceId, connectionId, data);
      if (response.status === 200) return response.data;
      throw toConnectionError(response.status, response.data);
    },
    onSuccess: (_connection, variables) => {
      void queryClient.invalidateQueries({
        queryKey: connectionKeys.list(workspaceId),
      });
      void queryClient.invalidateQueries({
        queryKey: connectionKeys.detail(workspaceId, variables.connectionId),
      });
      // A replaced token or host rewrites managed entries, and must be re-tested.
      void queryClient.invalidateQueries({
        queryKey: connectionKeys.health(workspaceId, variables.connectionId),
      });
      void queryClient.invalidateQueries({
        queryKey: secretKeys.all(workspaceId),
      });
    },
  });
}
